import { NavLink, Outlet } from "react-router-dom";
import { BarChart3, FileText, Building2, BookOpen, HardHat, Landmark, Settings2, Users, ScrollText, LogOut, Menu, X, ChevronDown, ChevronsLeft, ChevronsRight, CalendarDays } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { useFetch } from "../lib/api";
import { useResponsiveTables } from "../lib/responsive";

const NAV = [
  { group: "nav.group.ops", items: [
    { to: "/", key: "nav.dashboard", icon: BarChart3, perm: "dashboard", end: true },
    { to: "/quotations", key: "nav.quotations", icon: FileText, perm: "quote:read" },
    { to: "/schedule", key: "nav.schedule", icon: CalendarDays, perm: "schedule:read" },
  ] },
  { group: "nav.group.master", items: [
    { to: "/customers", key: "nav.customers", icon: Building2, perm: "master:read" },
    { to: "/catalog", key: "nav.catalog", icon: BookOpen, perm: "master:read" },
    { to: "/technicians", key: "nav.technicians", icon: HardHat, perm: "master:read" },
    { to: "/tax", key: "nav.tax", icon: Landmark, perm: "tax:read" },
  ] },
  { group: "nav.group.admin", items: [
    { to: "/settings", key: "nav.settings", icon: Settings2, perm: "master:read" },
    { to: "/users", key: "nav.users", icon: Users, perm: "users:manage" },
    { to: "/audit", key: "nav.audit", icon: ScrollText, perm: "audit:read" },
  ] },
];

const Sidebar = ({ onNav, collapsed, onToggle }) => {
  const { can } = useAuth();
  const { t } = useT();
  const [closed, setClosed] = useState(() => JSON.parse(localStorage.getItem("navClosed") || "{}"));
  const toggleGroup = (g) => { const n = { ...closed, [g]: !closed[g] }; setClosed(n); localStorage.setItem("navClosed", JSON.stringify(n)); };
  return (
    <nav className="flex h-full flex-col">
      <div className={`flex items-start justify-between pb-6 pt-6 ${collapsed ? "px-3" : "px-5"}`}>
        {collapsed ? <span className="mx-auto block h-5 w-5 bg-[#db0720]" /> : (
          <div>
            <img src="/brand/logo-bobst.svg" alt="BOBST" className="h-7" data-testid="sidebar-logo" />
            <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500">Service Quotation · LATAM</div>
          </div>
        )}
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto px-3">
        {NAV.map((g) => {
          const items = g.items.filter((i) => can(i.perm));
          if (!items.length) return null;
          const isClosed = !collapsed && closed[g.group];
          return (
            <div key={g.group}>
              {!collapsed && (
                <button onClick={() => toggleGroup(g.group)} data-testid={`nav-group-${g.group.split(".").pop()}`}
                  className="flex w-full items-center justify-between px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-400 hover:text-black">
                  {t(g.group)}<ChevronDown className={`h-3 w-3 transition-transform ${isClosed ? "-rotate-90" : ""}`} />
                </button>
              )}
              {!isClosed && items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.end} onClick={onNav} title={collapsed ? t(i.key) : undefined} data-testid={`nav-${i.to.replace("/", "") || "dashboard"}`}
                  className={({ isActive }) => `group relative flex items-center gap-3 rounded py-2 text-sm transition-colors ${collapsed ? "justify-center px-2" : "px-3"} ${isActive ? "bg-red-50 font-semibold text-[#db0720]" : "text-neutral-700 hover:bg-neutral-100 hover:text-black"}`}>
                  {({ isActive }) => (<>
                    <span className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[#db0720] transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`} />
                    <i.icon className="h-4 w-4 shrink-0" strokeWidth={isActive ? 2.4 : 1.8} />{!collapsed && t(i.key)}
                  </>)}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>
      {onToggle && (
        <button onClick={onToggle} data-testid="sidebar-collapse-btn" className="flex items-center gap-2 border-t border-neutral-200 px-5 py-3 text-xs text-neutral-500 hover:text-black">
          {collapsed ? <ChevronsRight className="mx-auto h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" />{t("nav.collapse")}</>}
        </button>
      )}
    </nav>
  );
};

const Header = ({ onMenu }) => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const { data: rate, reload: reloadRate } = useFetch("/exchange-rates/current");
  useEffect(() => {
    window.addEventListener("fx:changed", reloadRate);
    return () => window.removeEventListener("fx:changed", reloadRate);
  }, [reloadRate]);
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-neutral-200 bg-white/90 px-4 backdrop-blur-md lg:px-8">
      <button className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
      <div className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-2.5 py-1 text-[11px] sm:gap-2 sm:px-3 sm:text-xs" data-testid="header-fx-rate">
        <span className="font-semibold text-neutral-500">USD/MXN</span><span className="font-mono font-bold">{rate?.rate ? Number(rate.rate).toFixed(4) : "—"}</span>
      </div>
      <div className="hidden text-xs text-neutral-500 md:block" data-testid="header-scope">
        {t("usr.countries")}: <b className="text-neutral-800">{user.countries.includes("*") ? t("usr.allCountries") : user.countries.join(", ")}</b>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <div className="flex overflow-hidden rounded-full border border-neutral-300 text-xs font-bold">
          {["es", "en"].map((l) => (
            <button key={l} data-testid={`lang-selector-${l}`} onClick={() => setLang(l)}
              className={`px-3 py-1 transition-colors ${lang === l ? "bg-black text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>{l.toUpperCase()}</button>
          ))}
        </div>
        <div className="hidden text-right sm:block">
          <div className="text-xs font-semibold leading-tight" data-testid="header-user-name">{user.name}</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#db0720]" data-testid="header-user-role">{t(`role.${user.role}`)}</div>
        </div>
        <button onClick={logout} data-testid="logout-btn" title={t("common.logout")} className="rounded-full p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-black">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
};

export default function Layout() {
  useResponsiveTables();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(localStorage.getItem("navCollapsed") === "1");
  const toggle = () => { localStorage.setItem("navCollapsed", collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  return (
    <div className="flex min-h-screen bg-[#f6f6f6]">
      <aside data-testid="sidebar" className={`sticky top-0 hidden h-screen shrink-0 border-r border-neutral-200 bg-white transition-[width] duration-200 lg:block ${collapsed ? "w-16" : "w-64"}`}><Sidebar collapsed={collapsed} onToggle={toggle} /></aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
            <button className="absolute right-3 top-3" onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
            <Sidebar onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <Header onMenu={() => setOpen(true)} />
        <main className="page-enter mx-auto w-full max-w-[1600px] flex-1 px-4 py-6 lg:px-8 lg:py-8"><Outlet /></main>
      </div>
    </div>
  );
}
